import { Asleep, Enterprise, Light, Locked, Logout, Screen, User, UserAvatar } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { formatPhone } from '@/shared/format/phone';
import { Avatar, Menu, type MenuEntry } from '@/ui';
import { useServices, useSession } from '../AppServicesContext';
import { sectionForPath } from '../registry';
import { isStaffRole } from '../session/roles';
import { useTheme } from '../theme/ThemeProvider';
import s from '../layouts/Layout.module.css';
import { useMembershipLabel } from './ClinicSwitcher';

/** Меню профиля: тема, блокировка экрана, переход между порталом и бэк-офисом, выход. */
export function ProfileMenu({ area }: { area: 'clinic' | 'backoffice' }) {
  const { t } = useTranslation('shell');
  const { session } = useServices();
  const { state, membership } = useSession();
  const { preference, setPreference } = useTheme();
  const label = useMembershipLabel();
  const user = state.user;
  if (!user) return null;
  const isStaff = user.globalRoles.some(isStaffRole);

  const items: MenuEntry[] = [
    { type: 'group', id: 'theme-title', label: t('theme.title') },
    { type: 'radio', id: 'theme-light', label: t('theme.light'), icon: Light, checked: preference === 'light', onSelect: () => setPreference('light') },
    { type: 'radio', id: 'theme-dark', label: t('theme.dark'), icon: Asleep, checked: preference === 'dark', onSelect: () => setPreference('dark') },
    { type: 'radio', id: 'theme-system', label: t('theme.system'), icon: Screen, checked: preference === 'system', onSelect: () => setPreference('system') },
    { type: 'divider', id: 'divider-1' },
  ];
  /* Личный кабинет (профиль, язык, безопасность, клиники) — раздел features/account. */
  if (area === 'clinic' && sectionForPath('/account')) items.push({ id: 'account', label: t('profile.account'), icon: User, to: '/account' });
  if (area === 'clinic' && isStaff) items.push({ id: 'to-backoffice', label: t('profile.toBackoffice'), icon: Enterprise, to: '/backoffice' });
  if (area === 'backoffice' && user.memberships.length > 0) items.push({ id: 'to-portal', label: t('profile.toPortal'), icon: UserAvatar, to: '/' });
  items.push(
    { id: 'lock', label: t('profile.lock'), icon: Locked, onSelect: () => session.lock() },
    { id: 'logout', label: t('profile.signOut'), icon: Logout, danger: true, onSelect: () => void session.signOut() },
  );

  const roleLine =
    area === 'backoffice'
      ? user.globalRoles.map((role) => t(`roles.${role}`)).join(', ')
      : membership
        ? `${label.name(membership)} · ${label.roles(membership)}`
        : '';

  return (
    <Menu
      aria-label={t('profile.menuLabel')}
      items={items}
      header={
        <div className={s.profileHeader}>
          <span className={s.profileHeaderName}>{user.fullName}</span>
          <span className={s.profileHeaderMeta}>{formatPhone(user.phone)}</span>
          {roleLine ? <span className={s.profileHeaderMeta}>{roleLine}</span> : null}
        </div>
      }
      trigger={(props) => (
        <button type="button" className={s.profileButton} aria-label={t('profile.open', { name: user.fullName })} {...props}>
          <Avatar name={user.fullName} size="md" />
          <span className={s.profileName} aria-hidden="true">
            {user.fullName}
          </span>
        </button>
      )}
    />
  );
}
